/**
 * Mermaid Diagram Renderer
 *
 * Generates Mermaid flowchart syntax from service graphs.
 */

import type { GeoEventConfiguration, GeoEventService } from '../types/geoevent.js';
import type { ServiceGraph, GraphNodeType } from '../types/graph.js';
import type { MermaidDiagram, NodeStyle } from '../types/mermaid.js';
import { buildServiceGraph } from '../sorter/graph-builder.js';
import { topologicalSort } from '../sorter/topological-sort.js';

/**
 * Default node styles for different component types
 */
const DEFAULT_STYLES: NodeStyle = {
  inputStyle: 'fill:#90EE90,stroke:#228B22,stroke-width:2px',
  outputStyle: 'fill:#FFB6C1,stroke:#DC143C,stroke-width:2px',
  filterStyle: 'fill:#87CEEB,stroke:#4169E1,stroke-width:2px',
  processorStyle: 'fill:#DDA0DD,stroke:#8B008B,stroke-width:2px',
};

/**
 * Escapes special characters in labels for Mermaid syntax
 */
function escapeLabel(label: string): string {
  return label
    .replace(/"/g, "'")
    .replace(/\[/g, '(')
    .replace(/\]/g, ')')
    .replace(/</g, '(')
    .replace(/>/g, ')')
    .replace(/\{/g, '(')
    .replace(/\}/g, ')');
}

/**
 * Gets the node shape based on type
 * - Input: stadium shape (rounded rectangle) [( )]
 * - Output: stadium shape [( )]
 * - Filter: diamond shape { }
 * - Processor: rectangle [ ]
 */
function getNodeShape(id: string, label: string, type: GraphNodeType): string {
  const escapedLabel = escapeLabel(label);

  switch (type) {
    case 'input':
      return `${id}([${escapedLabel}])`;
    case 'output':
      return `${id}([${escapedLabel}])`;
    case 'filter':
      return `${id}{${escapedLabel}}`;
    case 'processor':
      return `${id}[${escapedLabel}]`;
    default:
      return `${id}[${escapedLabel}]`;
  }
}

/**
 * Gets the style class name for a node type
 */
function getStyleClass(type: GraphNodeType): string {
  switch (type) {
    case 'input':
      return 'inputNode';
    case 'output':
      return 'outputNode';
    case 'filter':
      return 'filterNode';
    case 'processor':
      return 'processorNode';
    default:
      return 'processorNode';
  }
}

/**
 * Renders a service graph as a Mermaid flowchart
 */
export function renderServiceGraph(
  graph: ServiceGraph,
  styles: NodeStyle = DEFAULT_STYLES
): MermaidDiagram {
  const lines: string[] = [];

  // Start flowchart
  lines.push('flowchart TD');

  // Add style definitions
  lines.push(`  classDef inputNode ${styles.inputStyle}`);
  lines.push(`  classDef outputNode ${styles.outputStyle}`);
  lines.push(`  classDef filterNode ${styles.filterStyle}`);
  lines.push(`  classDef processorNode ${styles.processorStyle}`);
  lines.push('');

  // Track which nodes have been defined
  const definedNodes = new Set<string>();

  // Add nodes with shapes
  for (const node of graph.nodes) {
    const nodeShape = getNodeShape(node.id, node.label, node.type);
    lines.push(`  ${nodeShape}`);
    definedNodes.add(node.id);
  }

  lines.push('');

  // Add edges
  for (const edge of graph.edges) {
    if (definedNodes.has(edge.from) && definedNodes.has(edge.to)) {
      lines.push(`  ${edge.from} --> ${edge.to}`);
    }
  }

  lines.push('');

  // Apply styles to nodes
  const nodesByType = new Map<GraphNodeType, string[]>();
  for (const node of graph.nodes) {
    const existing = nodesByType.get(node.type) ?? [];
    existing.push(node.id);
    nodesByType.set(node.type, existing);
  }

  for (const [type, nodeIds] of nodesByType) {
    if (nodeIds.length > 0) {
      const styleClass = getStyleClass(type);
      lines.push(`  class ${nodeIds.join(',')} ${styleClass}`);
    }
  }

  return {
    syntax: lines.join('\n'),
    nodeCount: graph.nodes.length,
    edgeCount: graph.edges.length,
    graph,
  };
}

/**
 * Renders a GeoEvent service as a Mermaid diagram
 */
export function renderService(
  service: GeoEventService,
  config: GeoEventConfiguration,
  styles: NodeStyle = DEFAULT_STYLES
): MermaidDiagram {
  const graph = buildServiceGraph(service, config);
  return renderServiceGraph(graph, styles);
}

/**
 * Renders all services in a configuration as Mermaid diagrams
 */
export function renderConfiguration(
  config: GeoEventConfiguration,
  styles: NodeStyle = DEFAULT_STYLES
): Map<string, MermaidDiagram> {
  const diagrams = new Map<string, MermaidDiagram>();

  for (const service of config.geoEventServices) {
    const diagram = renderService(service, config, styles);
    diagrams.set(service.name, diagram);
  }

  return diagrams;
}

/**
 * Renders a service with topological ordering annotation
 */
export function renderServiceWithOrder(
  service: GeoEventService,
  config: GeoEventConfiguration,
  styles: NodeStyle = DEFAULT_STYLES
): { diagram: MermaidDiagram; sortResult: ReturnType<typeof topologicalSort> } {
  const graph = buildServiceGraph(service, config);
  const sortResult = topologicalSort(graph);
  const diagram = renderServiceGraph(graph, styles);

  return { diagram, sortResult };
}
